// app/frontend/components/Ads/BannerAd.tsx
import { useState, useEffect } from 'react';
import { Card, Text, Button, Box, Group, Badge } from '@mantine/core';
import { IconExternalLink, IconAd } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';

interface BannerAdData {
  id: number;
  title: string;
  description?: string;
  url: string;
  banner_url?: string;
  ad_type: string;
  type: 'ad';
}

interface BannerAdProps {
  ads: BannerAdData[];
}

export default function BannerAd({ ads }: BannerAdProps) {
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Media queries for responsive design
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isSmallMobile = useMediaQuery('(max-width: 480px)');

  // Handle sequential rotation from session storage
  useEffect(() => {
    if (ads.length <= 1) return;

    const storageKey = 'banner_ad_rotation_index';
    const pageKey = `banner_ad_page_${window.location.pathname}`;

    // Check if we've already shown an ad for this specific page
    const pageAdIndex = sessionStorage.getItem(pageKey);

    if (pageAdIndex !== null) {
      // Page refresh - keep the same ad
      setCurrentAdIndex(parseInt(pageAdIndex, 10));
    } else {
      // New page navigation - advance to next ad
      const lastIndex = parseInt(sessionStorage.getItem(storageKey) || '0', 10);
      const nextIndex = (lastIndex + 1) % ads.length;

      setCurrentAdIndex(nextIndex);

      // Store both the global rotation index and this page's ad
      sessionStorage.setItem(storageKey, nextIndex.toString());
      sessionStorage.setItem(pageKey, nextIndex.toString());
    }
  }, [ads.length]);

  // Track impression when component mounts and ad is determined
  useEffect(() => {
    if (ads.length > 0 && currentAdIndex < ads.length) {
      const currentAd = ads[currentAdIndex];
      console.log('🎯 Tracking impression for ad ID:', currentAd.id);
      trackAdImpression(currentAd.id);
    }
  }, [currentAdIndex, ads]);

  // Track ad impression
  const trackAdImpression = async (adId: number) => {
    try {
      const response = await fetch(`/ads/${adId}/track_impression`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      });

      if (response.ok) {
        console.log('🎯 Impression tracked for ad ID:', adId);
      } else {
        console.error('🎯 Failed to track impression:', response.status);
      }
    } catch (error) {
      console.error('🎯 Impression tracking error:', error);
    }
  };

  if (!ads || ads.length === 0) {
    return null;
  }

  const currentAd = ads[currentAdIndex];

  // Handle banner click
  const handleBannerClick = (e: React.MouseEvent) => {
    // If clicking on a button inside the banner, don't trigger the banner click
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }

    // Track the click
    trackAdClick(currentAd.id);

    // Open ad URL in new tab
    if (currentAd.url) {
      window.open(currentAd.url, '_blank', 'noopener,noreferrer');
    }
  };

  // Track ad click with backend
  const trackAdClick = async (adId: number) => {
    console.log('🎯 Tracking click for ad ID:', adId);
    try {
      const response = await fetch(`/ads/${adId}/track_click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      });

      console.log('🎯 Click tracking response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('🎯 Click tracking success:', data);
      } else {
        console.error('🎯 Click tracking failed:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('🎯 Error response:', errorText);
      }
    } catch (error) {
      console.error('🎯 Click tracking error:', error);
    }
  };

  // Dynamic styles based on screen size
  const getMinHeight = () => {
    // Set a minimum height to ensure the card isn't too small
    if (isSmallMobile) return '140px';
    if (isMobile) return '160px';
    return '180px';
  };

  const getContentPadding = () => {
    if (isSmallMobile) return '0.75rem';
    if (isMobile) return '1rem';
    return '2rem';
  };

  const getContentDirection = () => {
    return isMobile ? 'column' : 'row';
  };

  const getTextMaxWidth = () => {
    return isMobile ? '100%' : '70%';
  };

  const getButtonSize = () => {
    if (isSmallMobile) return 'sm';
    if (isMobile) return 'md';
    return 'lg';
  };

  const getButtonStyle = () => {
    const baseStyle = {
      backgroundColor: '#EA580C',
      fontWeight: 600,
      boxShadow: '0 4px 15px rgba(234, 88, 12, 0.4)',
      transform: isHovered ? 'scale(1.05)' : 'scale(1)',
      transition: 'transform 0.2s ease',
    };

    if (isMobile) {
      return {
        ...baseStyle,
        width: '100%',
        marginTop: '0.5rem',
        padding: isSmallMobile ? '0.5rem 1rem' : '0.6rem 1.2rem',
        fontSize: isSmallMobile ? '0.875rem' : '0.9rem',
      };
    }

    return {
      ...baseStyle,
      padding: '0.75rem 1.5rem',
      fontSize: '1rem',
    };
  };

  const getTitleSize = () => {
    if (isSmallMobile) return 'lg';
    if (isMobile) return 'xl';
    return 'xl';
  };

  const getDescriptionSize = () => {
    if (isSmallMobile) return 'xs';
    if (isMobile) return 'xs';
    return 'md';
  };

  return (
    <Box
      mb="8px"
      style={{
        width: '100%',
        position: 'relative',
      }}
    >
      <Card
        padding={0}
        radius="md"
        style={{
          minHeight: getMinHeight(), // Use minHeight instead of height
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
          transform: isHovered ? 'scale(1.02)' : 'scale(1)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          boxShadow: isHovered ? '0 10px 30px rgba(234, 88, 12, 0.3)' : '0 4px 15px rgba(0, 0, 0, 0.1)',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleBannerClick}
      >
        {/* Background Image */}
        {currentAd.banner_url && (
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `url(${currentAd.banner_url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
        )}

        {/* Fallback Background for ads without images */}
        {!currentAd.banner_url && (
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, #EA580C 0%, #DC2626 100%)',
            }}
          />
        )}

        {/* Dark Overlay for text readability */}
        <Box
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.7) 100%)',
            zIndex: 1,
          }}
        />

        {/* Content Overlay */}
        <Box
          style={{
            position: 'relative', // Changed from absolute to relative
            zIndex: 2,
            display: 'flex',
            flexDirection: getContentDirection(),
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: isMobile ? 'space-between' : 'flex-start',
            padding: getContentPadding(),
            minHeight: getMinHeight(), // Ensure content area has minimum height
          }}
        >
          <Box
            style={{
              flex: isMobile ? '1' : '1',
              marginBottom: isMobile ? '0' : '0',
            }}
          >
            {/* Sponsored Badge */}
            <Badge
              variant="filled"
              color="#EA580C"
              size={isSmallMobile ? 'xs' : 'sm'}
              mb="4px"
              leftSection={<IconAd size={isSmallMobile ? 10 : 12} />}
              style={{
                backgroundColor: '#EA580C',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
              }}
            >
              Sponsored
            </Badge>

            {/* Ad Title */}
            <Text
              size={getTitleSize()}
              fw={700}
              style={{
                color: '#FFF7ED',
                marginBottom: '0.25rem',
                lineHeight: 1.2,
                textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)',
              }}
              className="rubik"
              lineClamp={isMobile ? undefined : 2} // Remove line clamp on mobile for full title
            >
              {currentAd.title}
            </Text>

            {/* Ad Description */}
            {currentAd.description && (
              <Text
                size={getDescriptionSize()}
                style={{
                  color: '#F3F4F6',
                  marginBottom: isMobile ? '0.5rem' : '1rem',
                  lineHeight: 1.3,
                  textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
                  maxWidth: getTextMaxWidth(),
                }}
                className="poppins"
                lineClamp={isMobile ? 3 : 2} // Allow more lines on mobile
              >
                {currentAd.description}
              </Text>
            )}
          </Box>

          {/* CTA Button */}
          <Button
            variant="filled"
            color="#EA580C"
            size={getButtonSize()}
            radius="md"
            rightSection={<IconExternalLink size={isSmallMobile ? 14 : 18} />}
            style={getButtonStyle()}
            className="poppins"
            onClick={(e) => {
              e.stopPropagation();
              trackAdClick(currentAd.id);
              window.open(currentAd.url, '_blank', 'noopener,noreferrer');
            }}
          >
            {isSmallMobile ? 'Learn More' : 'Learn More'}
          </Button>
        </Box>
      </Card>

      {/* Rotation Indicator (when multiple ads) */}
      {ads.length > 1 && (
        <Group justify="center" mt="xs" gap="4px">
          {ads.map((_, index) => (
            <Box
              key={index}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: index === currentAdIndex ? '#EA580C' : '#6B7280',
                transition: 'background-color 0.3s ease',
              }}
            />
          ))}
        </Group>
      )}
    </Box>
  );
}
